import {
  ReaImportViewState,
  ReaMessageState,
  ReaMetricItem,
  ReaTechnicalDetailItem,
  ReaUserMessage,
} from 'src/app/models/fincas/rea.model';

export interface ReaImportViewMapperInput {
  status: ReaMessageState;
  hasCurrentError: boolean;
  userMessage: ReaUserMessage | null;
  statusMessage: string;
  hasUserWarnings: boolean;
  customerMetrics: ReaMetricItem[];
  customerWarnings: string[];
  technicalDetails: ReaTechnicalDetailItem[];
}

/**
 * Converts the modal state into one small view model consumed by the template.
 */
export class ReaImportViewMapper {
  public static map(input: ReaImportViewMapperInput): ReaImportViewState {
    const state: ReaImportViewState = {
      status: input.status,
      title: this.title(input),
      description: this.description(input),
      customerMetrics: input.customerMetrics,
      customerWarnings: input.customerWarnings,
      technicalDetails: input.technicalDetails,
    };

    if (input.hasCurrentError) {
      state.nextStep = this.nextStep(input.userMessage);
    }

    return state;
  }

  private static title(input: ReaImportViewMapperInput): string {
    if (input.hasCurrentError) {
      return input.userMessage?.title ?? 'No se puede importar el REA';
    }

    if (input.status === 'success') {
      return input.hasUserWarnings ? 'Importación completada con avisos' : 'Importación completada';
    }

    if (input.status === 'selection') {
      return 'Selecciona el REA que quieres importar';
    }

    if (input.status === 'checking') {
      return 'Comprobando los datos REA';
    }

    if (input.status === 'warning') {
      return 'Faltan datos para importar REA';
    }

    return 'Importar REA';
  }

  private static description(input: ReaImportViewMapperInput): string {
    if (input.hasCurrentError) {
      return input.userMessage?.description
        ?? 'No hemos podido importar el REA. Revisa los datos indicados o contacta con soporte si el problema continúa.';
    }

    if (input.status === 'success') {
      return input.hasUserWarnings
        ? 'Los datos se han importado, pero hay parcelas o avisos que conviene revisar.'
        : 'Se han actualizado los datos oficiales del REA.';
    }

    if (input.status === 'selection') {
      return 'Hemos encontrado más de una explotación. Elige cuál quieres usar antes de importar.';
    }

    if (input.status === 'checking') {
      return 'Estamos verificando si el NIF/CIF tiene explotaciones REA asociadas.';
    }

    if (input.status === 'warning') {
      return 'Revisa que la finca tenga NIF/CIF y nombre antes de continuar.';
    }

    return 'Comprueba los datos y pulsa Importar cuando esté todo correcto.';
  }

  private static nextStep(userMessage: ReaUserMessage | null): string {
    return userMessage?.nextStep ?? 'Revisa los datos introducidos y vuelve a intentarlo.';
  }
}
